import { CSSResult, html, LitElement, nothing, PropertyValues, unsafeCSS } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property, state } from 'lit/decorators.js';
import { BrandFollowData } from '../../../@types/wishlist.js';
import styles from './brand-follow-button.scss?inline';
import { ADDED_BRAND_EVENT, config, REMOVED_BRAND_EVENT } from '../../../scripts/service/config.js';
import { brandService } from '../../../scripts/service/brand-service.js';
import { ButtonSize, parseButtonSize } from '../heart-button/button-size.js';
import { renderHeartIcon } from '../heart-button/heart-icons.js';
import { HeartAnimationController } from '../heart-button/heart-animation-controller.js';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';

@customElement('ents-brand-follow-button')
export class BrandFollowButtonElement extends LitElement {
  @property({ type: String, attribute: 'brand-code' }) private brandCode: string = '';

  @property({ type: String, attribute: 'brand-name' }) private brandName?: string = undefined;

  @property({ type: Boolean, attribute: 'show-text' }) private showText: boolean = false;

  @property({ type: Boolean, attribute: 'no-color' }) private noColor: boolean = false;

  @property({ type: String }) private placement?: string = undefined;

  @property({
    type: String,
    attribute: 'size',
    converter: { fromAttribute: (size) => parseButtonSize(size ?? '') },
  })
  private size: ButtonSize = ButtonSize.normal;

  @state()
  private followed: boolean = false;

  private readonly animation = new HeartAnimationController(this);

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles)];
  }

  private readonly onBrandAddedEvent: (e: Event) => void;

  private readonly onBrandRemovedEvent: (e: Event) => void;

  constructor() {
    super();

    this.onBrandAddedEvent = (e: Event) => {
      const data = (e as CustomEvent<BrandFollowData>).detail;
      if (data.brandCode === this.brandCode) {
        this.followed = true;
      }
    };

    this.onBrandRemovedEvent = (e: Event) => {
      const data = (e as CustomEvent<BrandFollowData>).detail;
      if (data.brandCode === this.brandCode) {
        this.followed = false;
        this.animation.resetImmediate();
      }
    };
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ADDED_BRAND_EVENT, this.onBrandAddedEvent);
    document.addEventListener(REMOVED_BRAND_EVENT, this.onBrandRemovedEvent);

    if (this.brandCode) {
      brandService.requestBrandState(this.brandCode);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ADDED_BRAND_EVENT, this.onBrandAddedEvent);
    document.removeEventListener(REMOVED_BRAND_EVENT, this.onBrandRemovedEvent);
  }

  shouldUpdate(changedProperties: PropertyValues) {
    if (this.animation.shouldBlockUpdate()) return false;
    return super.shouldUpdate(changedProperties);
  }

  protected willUpdate(changedProperties: PropertyValues) {
    super.willUpdate(changedProperties);
    if (changedProperties.has('brandCode') && changedProperties.get('brandCode') !== undefined) {
      this.followed = false;
      if (this.brandCode) {
        brandService.requestBrandState(this.brandCode);
      }
    }
  }

  protected updated(changedProperties: PropertyValues) {
    super.updated(changedProperties);
    this.setAttribute('selected', this.isFollowed() ? 'true' : 'false');
  }

  private isFollowed() {
    return this.followed;
  }

  private getLabel() {
    if (this.isFollowed()) {
      return config.messageBrandFollowTextActive;
    }
    return config.messageBrandFollowTextInactive;
  }

  private bdkButtonSize() {
    if (!this.showText) {
      return null;
    }
    if (this.size === ButtonSize.normal) {
      return 'medium';
    } else if (this.size === ButtonSize.small) {
      return 'small';
    } else if (this.size === ButtonSize.large) {
      return 'large';
    }
    return null;
  }

  private handleClick(e: Event) {
    e.stopPropagation();
    if (!this.isFollowed()) {
      this.animation.trigger();
    }
    const eventName = this.isFollowed() ? 'ents:brand:remove' : 'ents:brand:add';
    const detail: BrandFollowData = {
      brandCode: this.brandCode,
      brandName: this.brandName,
      tracking: {
        placement: this.placement,
        context: this.isFollowed() ? 'remove_heart' : 'add_heart',
      },
    };
    document.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  private renderContent() {
    const renderLabel = () => {
      if (this.showText) {
        return html`<span class="label">${this.getLabel()}</span>`;
      }
      return '';
    };
    return html`${renderHeartIcon(this.size, this.isFollowed())}${renderLabel()}`;
  }

  protected render() {
    return html`
      <label
        aria-label="${config.messageBrandFollowAriaLabel}"
        @click="${(e: Event) => e.stopPropagation()}"
        variant="${this.showText ? 'outline' : nothing}"
        size="${this.bdkButtonSize()}"
        class="ents-brand-follow-button ${classMap({
          selected: this.isFollowed(),
          'no-color': this.noColor,
          'bdk-button': this.showText,
          text: this.showText,
          pulse: this.animation.active,
          'no-description': !this.showText,
        })}"
      >
        <input
          type="checkbox"
          aria-checked="${this.isFollowed()}"
          ?checked="${this.isFollowed()}"
          @click="${this.handleClick}"
          data-test-ents-brand-followed="${this.isFollowed()}"
          class="sr-only"
        />
        ${this.renderContent()}
      </label>
    `;
  }
}
